import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../hooks/useSession';

interface HeaderProps {
  backTo?: string;
  backLabel?: string;
}

// py-2 -my-2 grows the tap target to ~40px tall without changing the visual
// layout — easier to hit on mobile, and clearer of adjacent links.
const navLinkClass =
  'text-sm text-charcoal-light hover:text-amber transition-colors duration-300 py-2 -my-2';

const Header: React.FC<HeaderProps> = ({ backTo, backLabel }) => {
  const { session, signOut } = useSession();
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSignOutError(null);
    setSigningOut(true);
    const { error } = await signOut();
    setSigningOut(false);
    if (error) {
      setSignOutError(error.message);
    }
  };

  const brand = (
    <Link
      to="/"
      className="font-display text-xl text-charcoal hover:text-amber transition-colors duration-300"
    >
      Rankum
    </Link>
  );

  return (
    <nav
      aria-label="Site navigation"
      className="sticky top-0 z-20 bg-cream/80 backdrop-blur-xl border-b border-black/5"
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            {backTo ? (
              <Link
                to={backTo}
                aria-label={backLabel}
                className="flex items-center gap-2 py-2 -my-2 text-charcoal-light hover:text-amber transition-colors duration-300"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                </svg>
                {/* Hide the label on small screens so the signed-in nav
                    (My List / Account / Sign out) doesn't overflow; the arrow
                    + aria-label keep it usable. */}
                <span className="text-sm hidden sm:inline">{backLabel}</span>
              </Link>
            ) : (
              brand
            )}
          </div>
          <div className="flex items-center gap-4">
            {backTo && <span className="hidden sm:inline">{brand}</span>}
            {session ? (
              <>
                <Link to="/my-list" className={navLinkClass}>
                  My List
                </Link>
                <Link to="/account" className={navLinkClass}>
                  Account
                </Link>
                <button
                  type="button"
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className={`${navLinkClass} disabled:opacity-50`}
                >
                  {signingOut ? 'Signing out…' : 'Sign out'}
                </button>
              </>
            ) : (
              <Link to="/sign-in" className={navLinkClass}>
                Sign in
              </Link>
            )}
          </div>
        </div>
        {signOutError && (
          <p
            role="alert"
            className="container mx-auto px-6 pt-2 text-xs text-terracotta text-right"
          >
            Sign out failed: {signOutError}
          </p>
        )}
      </div>
    </nav>
  );
};

export default Header;
